import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { utilityApiRef } from '@mercedes-benz/backstage-plugin-aiecosystem';

import { EntityNameValidatorUiOptions } from './schema';
import { ValidationState } from './EntityNameValidator.types';

/**
 * Provides state and validation behavior for the entity name validator.
 * @param props - Field extension component props
 * @returns Entity name validator state and event handlers
 */
export const useEntityNameValidator = (
  props: FieldExtensionComponentProps<string, EntityNameValidatorUiOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    schema: {
      title = 'Entity Name',
      description = 'Only letters, numbers, and spaces allowed.',
      maxLength = 40,
      pattern = '^[a-zA-Z0-9 ]+$',
    },
    uiSchema, // > ui:options, ui:placeholder
    formData,
    formContext, // Access to the entire form data for extracting Entity ID
  } = props;

  // Extract UI options from the schema
  const uiOptions =
    (uiSchema?.['ui:options'] as EntityNameValidatorUiOptions) ?? {};

  const debounceMs = uiOptions.debounceMs ?? 400;
  const isEditMode = uiOptions.isEditMode ?? false;
  const type = uiOptions.type ?? 'entity';
  const typeDisplayName = uiOptions.typeDisplayName ?? type;
  const placeholder =
    (uiSchema?.['ui:placeholder'] as string | undefined) ?? 'e.g. My Entity';

  const utilityApi = useApi(utilityApiRef);
  const [entityName, setEntityName] = useState<string>(formData ?? '');
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Save Name from formData (normalized: lowercase, no spaces)
  const originalNameRef = useRef<string | null>(
    isEditMode && formData
      ? formData.trim().toLowerCase().replace(/\s+/g, '')
      : null,
  );

  // Extract agent ID from formContext if in Edit mode
  // This ID will be used as exclude_agent_id parameter in the API call
  const agentIdRef = useRef<string | null>(
    isEditMode && formContext?.formData?.agentId
      ? formContext.formData.agentId
      : null,
  );

  // Extract mcp server ID from formContext if in Edit mode
  // This ID will be used as exclude_agent_id parameter in the API call
  const mcpServerIdRef = useRef<string | null>(
    isEditMode && formContext?.formData?.mcpServerId
      ? formContext.formData.mcpServerId
      : null,
  );

  // Extract entity ID based on type
  const entityIdRef = useRef<string | null>(
    type === 'agent' ? agentIdRef.current : mcpServerIdRef.current,
  );

  const hasRawErrors = rawErrors && rawErrors.length > 0;
  const isInvalid =
    hasRawErrors ||
    validationState === 'invalid' ||
    validationState === 'error';
  const isValid = validationState === 'valid';
  const isValidating = validationState === 'validating';

  /**
   * Check if validation should be skipped (early return)
   * @param value - The entity name to validate
   * @returns true if validation should be skipped, false otherwise
   */
  const checkEarlyReturnValidation = useCallback(
    (value: string) => {
      // Empty value handling
      if (!value || value.trim().length === 0) {
        setValidationState('error');
        setValidationError('Error. This field is required.');
        onChange(undefined as any);
        return true;
      }

      // Length validation
      if (value.length > maxLength) {
        setValidationState('invalid');
        setValidationError(
          `The ${typeDisplayName} name can be up to ${maxLength} characters long. Current: ${value.length} characters.`,
        );
        onChange(undefined as any);
        return true;
      }

      // Pattern validation
      if (!new RegExp(pattern).test(value)) {
        setValidationState('error');
        setValidationError('Only letters, numbers, and spaces allowed.');
        onChange(undefined as any);
        return true;
      }

      setValidationState('validating');
      setValidationError(null);
      return false;
    },
    [maxLength, typeDisplayName, pattern, onChange],
  );

  /**
   * Call the agent name validation API
   * @param name - The entity name to validate
   */
  const callEntityNameValidationApi = useCallback(
    async (name: string) => {
      try {
        // Call API with optional exclude_agent_id parameter
        // In Edit mode: Pass the agent ID to allow keeping the same name
        // In Onboarding mode: Don't pass agent ID, all existing names are blocked
        const excludeId =
          isEditMode && entityIdRef.current ? entityIdRef.current : undefined;
        const result = await utilityApi.getNameAvailability(
          name,
          type,
          excludeId,
        );

        if (result.available) {
          setValidationState('valid');
          setValidationError(null);
          onChange(name);
        } else {
          setValidationState('invalid');
          const errorMessage = `Error. The ${typeDisplayName} name "${name}" already exists. Please provide a different name for your ${typeDisplayName}.`;
          setValidationError(errorMessage);
          onChange(undefined as any);
        }
      } catch (err) {
        setValidationState('error');
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Unknown error occurred while checking name availability.';
        setValidationError(errorMessage);
        onChange(undefined as any);
      }
    },
    [isEditMode, onChange, type, typeDisplayName, utilityApi],
  );

  /**
   * Performs validation of the entity name against the Name Validation API.
   * Implements debouncing to avoid excessive API calls while typing.
   * Handles both Edit mode (allows keeping the same name) and Onboarding mode (all existing names blocked).
   *
   * @param value - The entity name to validate
   */
  const performValidation = useCallback(
    (value: string) => {
      if (checkEarlyReturnValidation(value)) {
        return;
      }

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounced validation logic
      debounceTimerRef.current = setTimeout(async () => {
        // In Edit mode: Check if new name equals saved name from formData
        // If yes, skip API call as the name hasn't changed
        if (
          isEditMode &&
          value.toLowerCase().replace(/\s+/g, '') === originalNameRef.current
        ) {
          setValidationState('valid');
          setValidationError(null);
          onChange(value);
          return;
        }

        callEntityNameValidationApi(value);
      }, debounceMs);
    },
    [
      callEntityNameValidationApi,
      checkEarlyReturnValidation,
      debounceMs,
      isEditMode,
      onChange,
    ],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Perform initial validation if formData is present (e.g., after "Start over")
  useEffect(() => {
    if (formData && formData.trim().length > 0) {
      performValidation(formData);
    }
  }, [formData, performValidation]);

  /**
   * Updates the controlled entity name and starts validation.
   * @param event - Input change event
   */
  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const val = event.target.value;
    setEntityName(val);
    performValidation(val);
  }

  /**
   * Gets the helper text for the current validation state.
   * @returns Helper text to display below the input
   */
  function getHelperText() {
    if (validationError) {
      return validationError;
    }

    if (hasRawErrors) {
      return rawErrors[0];
    }

    if (isValidating) {
      return isEditMode ? '' : 'Checking availability...';
    }

    if (isValid && entityName.trim().length > 0) {
      if (
        isEditMode &&
        originalNameRef.current &&
        entityName.trim().toLowerCase().replace(/\s+/g, '') ===
          originalNameRef.current
      ) {
        return '';
      }
      return `Success! The ${typeDisplayName} name "${entityName}" is available.`;
    }

    return description;
  }

  return {
    entityName,
    getHelperText,
    handleInputChange,
    isEditMode,
    isInvalid,
    isValid,
    isValidating,
    maxLength,
    pattern,
    placeholder,
    required,
    title,
  };
};
